import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { mappingRoutes } from "src/app/core/mappingRoutes";
import { NzBreadCrumbModule } from "ng-zorro-antd/breadcrumb";
import { NzAvatarModule } from "ng-zorro-antd/avatar";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzIconModule } from "ng-zorro-antd/icon";
import { AuthService } from "src/app/services/auth.service";
import { UtilsService } from "src/app/services/utils.service";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzModalModule, NzModalService } from "ng-zorro-antd/modal";
import { ChangePasswordModalComponent } from "src/app/shared/modals/change-password-modal/change-password-modal.component";
import { ComplaintService } from "src/app/services/complaint.service";
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, EMPTY } from "rxjs";
import { FormsModule } from "@angular/forms";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzEmptyModule } from "ng-zorro-antd/empty";
import { NzTagModule } from "ng-zorro-antd/tag";
import { IComplaint } from "src/app/core/interfaces/IComplaint";

@Component({
  selector: "navbar-cmp",
  templateUrl: "navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    NzInputModule,
    NzAvatarModule,
    NzBreadCrumbModule,
    NzIconModule,
    NzDropDownModule,
    NzMenuModule,
    NzModalModule,
    FormsModule,
    NzSpinModule,
    NzEmptyModule,
    NzTagModule,
  ],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input("routeMenu") ROUTE_MENU: any[] = [];
  titleModule: string = "";
  private listTitles: any[] = [];
  isMobileMenuOpen: boolean = false;
  sidebarVisible: boolean = false;
  private readonly onResize = () => this.handleResize();
  profile: any;

  // Search
  searchQuery: string = "";
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  isSearching: boolean = false;
  searchResults: IComplaint[] = [];
  showSearchResults: boolean = false;

  // User menu (avatar dropdown) — controlled visibility for Esc / outside-click
  userMenuVisible: boolean = false;
  userMenuMobileVisible: boolean = false;

  @HostListener("document:keydown.escape")
  onEscapeKey() {
    this.userMenuVisible = false;
    this.userMenuMobileVisible = false;
  }

  constructor(
    private router: Router,
    private _service: AuthService,
    private _utilService: UtilsService,
    private modalService: NzModalService,
    private complaintService: ComplaintService
  ) {
    this._service.admin$.subscribe((response) => {
      this.profile = response;
    });
  }

  ngOnInit() {
    this.listTitles = this.ROUTE_MENU.filter((listTitle: any) => listTitle);
    this.titleModule = this.listTitles[0]["title"];

    // Initialize sidebar state based on screen size
    this.sidebarVisible = window.innerWidth >= 992;

    this.handleResize();
    window.addEventListener("resize", this.onResize);

    this.router.events.subscribe(() => {
      // Fermer la sidebar uniquement sur mobile lors de la navigation
      if (window.innerWidth < 992) {
        this.sidebarClose();
      }
      this.getTitleMenu();
    });

    // Setup search with debounce
    this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (!query || query.trim().length < 2) {
            this.searchResults = [];
            this.showSearchResults = false;
            return EMPTY;
          }
          this.isSearching = true;
          this.showSearchResults = true;
          return this.complaintService.search(query.trim());
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.isSearching = false;
          if (response && response.statusCode === 200) {
            this.searchResults = response.data || [];
            this.showSearchResults = true;
          } else {
            this.searchResults = [];
          }
        },
        error: () => {
          this.isSearching = false;
          this.searchResults = [];
        },
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener("resize", this.onResize);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogOut() {
    // AuthService.logout() reset _admin (BehaviorSubject) en plus du token
    // et du tenantContext — sinon les composants abonnés à admin$ gardent
    // l'utilisateur précédent et l'UI reste comme connectée.
    this._service.logout();
    this.router.navigate([mappingRoutes.login]);
  }
  sidebarToggle() {
    this.sidebarVisible = !this.sidebarVisible;
    this.updateSidebarState();
  }

  sidebarOpen() {
    this.sidebarVisible = true;
    this.updateSidebarState();
  }

  sidebarClose() {
    this.sidebarVisible = false;
    this.updateSidebarState();
  }

  private updateSidebarState() {
    const html = document.documentElement;
    const mainPanel = document.querySelector<HTMLElement>(".main-panel");
    const isDesktop = window.innerWidth >= 992;

    if (isDesktop) {
      // Desktop: use sidebar-collapsed class
      if (this.sidebarVisible) {
        html.classList.remove("sidebar-collapsed");
      } else {
        html.classList.add("sidebar-collapsed");
      }
    } else {
      // Mobile: use nav-open class
      if (this.sidebarVisible) {
        html.classList.add("nav-open");
        if (mainPanel) {
          mainPanel.style.position = "fixed";
        }
      } else {
        html.classList.remove("nav-open");
        if (mainPanel) {
          mainPanel.style.position = "";
        }
      }
    }
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  private handleResize() {
    const html = document.documentElement;
    const isDesktop = window.innerWidth >= 992;

    // Desktop: ensure the mobile panel is closed and reset classes
    if (isDesktop) {
      this.isMobileMenuOpen = false;
      html.classList.remove("nav-open");
      // Keep sidebar visible state on desktop, just update the class
      if (!this.sidebarVisible) {
        html.classList.add("sidebar-collapsed");
      } else {
        html.classList.remove("sidebar-collapsed");
      }
    } else {
      // Mobile: remove desktop class
      html.classList.remove("sidebar-collapsed");
      // Reset sidebar to closed on mobile
      this.sidebarVisible = false;
      html.classList.remove("nav-open");
    }
  }

  getTitleMenu() {
    // Filtre les entrées de menu dont le `prefix` apparaît dans l'URL
    // courante. Les entrées avec `prefix: ""` matchent toujours via
    // String.includes("") → toujours `true` ; à éviter (voir
    // dashboard-routes.routes.ts pour des prefix non vides).
    // `reverse()[0]` prend la dernière entrée matching (la plus
    // spécifique attendue) ; fallback sur la 1ère entrée du menu sinon.
    const titles = this.listTitles.filter(
      (value) =>
        !!value?.["prefix"] && this.router.url.includes(value["prefix"])
    );
    this.titleModule =
      titles.reverse()[0]?.["title"] ?? this.listTitles[0]?.["title"] ?? "";
  }

  goProfile() {
    this.router.navigate([mappingRoutes.myProfile]);
  }
  
  openChangePasswordModal() {
    this.modalService.create({
      nzTitle: "Modifier le mot de passe",
      nzContent: ChangePasswordModalComponent,
      nzFooter: null,
      nzMaskClosable: false,
      nzWidth: 520,
    });
  }
  
  goNotification() {
    this.router.navigate([mappingRoutes.notification]);
  }
  
  getInitials(): string {
    if (!this.profile) return "U";
    const firstName = this.profile.firstName || "";
    const lastName = this.profile.lastName || "";
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return firstInitial + lastInitial || "U";
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  onSearchSubmit() {
    if (this.searchQuery && this.searchQuery.trim().length >= 2) {
      this.searchSubject.next(this.searchQuery);
    }
  }

  goToComplaintDetails(id: string) {
    this.showSearchResults = false;
    this.searchQuery = "";
    this.searchResults = [];
    this.router.navigate([mappingRoutes.plaintes.details, id]);
  }

  closeSearchResults() {
    this.showSearchResults = false;
  }

  onSearchFocus() {
    if (this.searchResults.length > 0) {
      this.showSearchResults = true;
    }
  }
}
